/**
 * 클라이언트 피벗 — 행/열/값 집계 (엑셀 피벗 1차)
 */

export const PIVOT_AGG_OPTIONS = [
  { value: 'sum', label: '합계' },
  { value: 'count', label: '건수' },
  { value: 'avg', label: '평균' },
  { value: 'min', label: '최소' },
  { value: 'max', label: '최대' },
];

function pathKey(row, fields) {
  return (fields || []).map((f) => String(row?.[f] ?? '')).join('\u0001');
}

function pathLabel(row, fields) {
  return (fields || []).map((f) => String(row?.[f] ?? '')).join(' / ');
}

function toNum(v) {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

function aggregate(nums, valuesLen, agg) {
  if (agg === 'count') return valuesLen;
  if (!nums.length) return agg === 'sum' || agg === 'avg' ? 0 : '';
  if (agg === 'sum') return nums.reduce((a, b) => a + b, 0);
  if (agg === 'avg') return nums.reduce((a, b) => a + b, 0) / nums.length;
  if (agg === 'min') return Math.min(...nums);
  if (agg === 'max') return Math.max(...nums);
  return nums.reduce((a, b) => a + b, 0);
}

/**
 * @param {object[]} rows
 * @param {{ rowFields: string[], colFields: string[], valueField: string, agg: string, fieldLabels?: Record<string,string> }} spec
 * @returns {{ columns: {id:string, header:string}[], data: object[], colKeys: string[] }}
 */
export function buildPivotTable(rows, spec) {
  const rowFields = (spec.rowFields || []).filter(Boolean);
  const colFields = (spec.colFields || []).filter(Boolean);
  const valueField = spec.valueField || '';
  const agg = spec.agg || 'sum';
  const labels = spec.fieldLabels || {};

  if (!rowFields.length && !colFields.length) {
    return { columns: [], data: [], colKeys: [] };
  }

  const bucket = new Map();
  const colKeySet = new Set();
  const rowOrder = [];
  const rowLabelByKey = new Map();

  for (const row of rows || []) {
    const rKey = rowFields.length ? pathKey(row, rowFields) : '(전체)';
    const cKey = colFields.length ? pathKey(row, colFields) : '_value';
    if (!bucket.has(rKey)) {
      bucket.set(rKey, new Map());
      rowOrder.push(rKey);
      rowLabelByKey.set(rKey, rowFields.length ? pathLabel(row, rowFields) : '전체');
    }
    const colMap = bucket.get(rKey);
    if (!colMap.has(cKey)) colMap.set(cKey, []);
    colMap.get(cKey).push(valueField ? row[valueField] : 1);
    colKeySet.add(cKey);
  }

  const colKeys = colFields.length
    ? [...colKeySet].sort((a, b) => String(a).localeCompare(String(b), 'ko'))
    : ['_value'];

  const rowHeader = rowFields.length
    ? rowFields.map((f) => labels[f] || f).join(' / ')
    : '구분';

  const columns = [
    { id: '_row', header: rowHeader },
    ...colKeys.map((k) => ({
      id: k === '_value' ? '_val' : k,
      header: k === '_value'
        ? `${labels[valueField] || valueField || '값'} (${PIVOT_AGG_OPTIONS.find((o) => o.value === agg)?.label || agg})`
        : k.split('\u0001').join(' / '),
    })),
  ];

  const data = rowOrder.map((rKey) => {
    const rec = { _row: rowLabelByKey.get(rKey) };
    const colMap = bucket.get(rKey);
    for (const cKey of colKeys) {
      const list = colMap.get(cKey) || [];
      const nums = list.map(toNum).filter((n) => n != null);
      const field = cKey === '_value' ? '_val' : cKey;
      rec[field] = aggregate(nums, list.length, agg);
    }
    return rec;
  });

  return { columns, data, colKeys };
}
